/**
 * MockFactoryStore — web mirror of `Sources/Store/MockFactoryStore.swift`.
 *
 * In-memory store with realistic sample data so the app renders standalone.
 * THIS is what the shell runs on today. Enqueued commands are appended to a
 * local list and reflected into the read-model so Chat/approvals feel live
 * without a backend.
 */

import {
  type FactoryApprovalRow,
  type FactoryCommandInsert,
  type FactoryCommandRow,
  type FactoryEventRow,
  type FactoryHealthRow,
  type FactoryPrTaskRow,
  type FactoryTaskRow,
  type Json,
  type TenantId,
} from '../models/factory';
import { StubAuth } from '../auth/AuthBoundary';
import { fetchSnapshotDefault, type FactorySnapshot, type FactoryStore } from './FactoryStore';

const MOCK_LATENCY_MS = 250;

function uuid(): string {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export class MockFactoryStore implements FactoryStore {
  private readonly tenant: TenantId;
  private tasks: FactoryTaskRow[];
  private prTasks: FactoryPrTaskRow[];
  private approvals: FactoryApprovalRow[];
  private events: FactoryEventRow[];
  private health: FactoryHealthRow;
  private commands: FactoryCommandRow[] = [];
  private nextEventId: number;

  constructor(tenant: TenantId = StubAuth.fakeTenantId) {
    this.tenant = tenant;
    const seed = buildSeed(tenant);
    this.tasks = seed.tasks;
    this.prTasks = seed.prTasks;
    this.approvals = seed.approvals;
    this.events = seed.events;
    this.health = seed.health;
    this.nextEventId = Math.max(...seed.events.map((e) => e.id), 1000) + 1;
  }

  private async delay(): Promise<void> {
    await new Promise((r) => setTimeout(r, MOCK_LATENCY_MS));
  }

  async fetchTasks(): Promise<FactoryTaskRow[]> {
    await this.delay();
    return [...this.tasks].sort((a, b) => (a.priority ?? 99) - (b.priority ?? 99));
  }

  async fetchPRTasks(): Promise<FactoryPrTaskRow[]> {
    await this.delay();
    return this.prTasks;
  }

  async fetchApprovals(): Promise<FactoryApprovalRow[]> {
    await this.delay();
    return this.approvals.filter((a) => (a.status ?? 'pending') === 'pending');
  }

  async fetchEvents(limit: number): Promise<FactoryEventRow[]> {
    await this.delay();
    return [...this.events]
      .sort((a, b) => Date.parse(b.event_ts) - Date.parse(a.event_ts))
      .slice(0, limit);
  }

  async fetchHealth(): Promise<FactoryHealthRow | null> {
    await this.delay();
    return this.health;
  }

  async fetchSnapshot(): Promise<FactorySnapshot> {
    return fetchSnapshotDefault(this);
  }

  async submit(command: FactoryCommandInsert): Promise<FactoryCommandRow> {
    await this.delay();
    const now = new Date();
    const nowISO = now.toISOString();
    const cmd: FactoryCommandRow = {
      id: uuid(),
      tenant_id: this.tenant,
      kind: command.kind,
      payload: command.payload ?? null,
      status: 'queued',
      result: null,
      created_at: nowISO,
      applied_at: null,
      updated_at: nowISO,
    };
    this.commands.push(cmd);

    // Reflect the command into the local read-model so the UI feels live.
    this.events.push({
      id: this.nextEventId++,
      tenant_id: this.tenant,
      type: `command.${command.kind}`,
      scope: 'app',
      payload: command.payload ?? null,
      event_ts: nowISO,
      created_at: nowISO,
    });

    // A posted chat message shows up as a follow-up ack a moment later.
    if (command.kind === 'post_chat') {
      this.events.push({
        id: this.nextEventId++,
        tenant_id: this.tenant,
        type: 'chat.ack',
        scope: 'app',
        payload: { note: 'Queued — engine will pick this up.' },
        event_ts: new Date(now.getTime() + 1000).toISOString(),
        created_at: nowISO,
      });
    }

    // Resolving an approval removes it from the pending queue.
    if (command.kind === 'resolve_approval') {
      const externalId = extractString(command.payload, 'external_id');
      if (externalId) this.approvals = this.approvals.filter((a) => a.external_id !== externalId);
    }

    return cmd;
  }
}

function extractString(payload: Json | undefined, key: string): string | null {
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    const v = (payload as Record<string, Json | undefined>)[key];
    if (typeof v === 'string') return v;
  }
  return null;
}

// MARK: - Seed data (ported from MockData.seed)

interface Seed {
  tasks: FactoryTaskRow[];
  prTasks: FactoryPrTaskRow[];
  approvals: FactoryApprovalRow[];
  events: FactoryEventRow[];
  health: FactoryHealthRow;
}

function buildSeed(tenant: TenantId): Seed {
  const now = Date.now();
  const ago = (minutes: number) => new Date(now - minutes * 60_000).toISOString();

  const tasks: FactoryTaskRow[] = [
    {
      id: uuid(), tenant_id: tenant, external_id: 'task-4821', department: 'gtm', priority: 1,
      status: 'running', source: 'loop:gtm', worktree_repo: 'trendy-stream', branch: 'gtm/winback-drip',
      log_path: '/var/log/factory/task-4821.log', budget_usd: 5.0, runtime_sec: 812,
      current_action: 'Drafting winback email variant B', result: null, error: null,
      started_at: ago(14), finished_at: null, created_at: ago(20), updated_at: ago(1),
    },
    {
      id: uuid(), tenant_id: tenant, external_id: 'task-4819', department: 'product', priority: 2,
      status: 'running', source: 'brief', worktree_repo: 'factory-app', branch: 'feat/notifications-feed',
      log_path: '/var/log/factory/task-4819.log', budget_usd: 8.0, runtime_sec: 245,
      current_action: 'Wiring events feed to store', result: null, error: null,
      started_at: ago(4), finished_at: null, created_at: ago(6), updated_at: ago(0.5),
    },
    {
      id: uuid(), tenant_id: tenant, external_id: 'task-4805', department: 'finance', priority: 3,
      status: 'blocked', source: 'loop:backoffice', worktree_repo: 'agent-orchestrator', branch: 'fin/plaid-reconcile',
      log_path: '/var/log/factory/task-4805.log', budget_usd: 3.0, runtime_sec: 1320,
      current_action: 'Awaiting approval: send receipt email', result: null, error: null,
      started_at: ago(60), finished_at: null, created_at: ago(75), updated_at: ago(9),
    },
    {
      id: uuid(), tenant_id: tenant, external_id: 'task-4798', department: 'product', priority: 4,
      status: 'done', source: 'brief', worktree_repo: 'nouri-ios-app', branch: 'feat/liquid-glass-tabs',
      log_path: '/var/log/factory/task-4798.log', budget_usd: 6.0, runtime_sec: 2044,
      current_action: null, result: 'Shipped build 9 to TestFlight', error: null,
      started_at: ago(180), finished_at: ago(120), created_at: ago(200), updated_at: ago(120),
    },
    {
      id: uuid(), tenant_id: tenant, external_id: 'task-4790', department: 'infra', priority: 5,
      status: 'failed', source: 'loop:backoffice', worktree_repo: 'trendy-stream', branch: 'infra/render-scale',
      log_path: '/var/log/factory/task-4790.log', budget_usd: 4.0, runtime_sec: 410,
      current_action: null, result: null, error: 'render-server OOM at 3 concurrent jobs; needs limit bump',
      started_at: ago(300), finished_at: ago(292), created_at: ago(310), updated_at: ago(292),
    },
  ];

  const prTasks: FactoryPrTaskRow[] = [
    {
      id: uuid(), tenant_id: tenant, pr_key: 'sully-k/factory-app#3', status: 'review',
      verdict: 'approve_with_nits', sensitive: false, preview_url: 'https://preview.factory.dev/pr-3',
      clickup_ticket_id: '86xxab12', created_at: ago(30), updated_at: ago(3),
    },
    {
      id: uuid(), tenant_id: tenant, pr_key: 'Trendy-Video/trendy-stream#1287', status: 'changes_requested',
      verdict: 'block', sensitive: true, preview_url: null, clickup_ticket_id: '86xxcd34',
      created_at: ago(90), updated_at: ago(12),
    },
    {
      id: uuid(), tenant_id: tenant, pr_key: 'sully-k/agent-orchestrator#54', status: 'merged',
      verdict: 'approve', sensitive: false, preview_url: null, clickup_ticket_id: null,
      created_at: ago(240), updated_at: ago(150),
    },
  ];

  const approvals: FactoryApprovalRow[] = [
    {
      id: uuid(), tenant_id: tenant, external_id: 'appr-991', task_ref: 'task-4805', tool: 'gmail.send',
      args: { to: 'client@example.com', subject: 'Your July receipt', attachment: 'receipt-jul.pdf' },
      status: 'pending', decision: null, reason: null, created_at: ago(9), updated_at: ago(9),
    },
    {
      id: uuid(), tenant_id: tenant, external_id: 'appr-987', task_ref: 'task-4819', tool: 'github.merge_pull_request',
      args: { repo: 'sully-k/factory-app', pr: 3, method: 'squash' },
      status: 'pending', decision: null, reason: null, created_at: ago(3), updated_at: ago(3),
    },
    {
      id: uuid(), tenant_id: tenant, external_id: 'appr-980', task_ref: 'task-4790', tool: 'shell.exec',
      args: { cmd: 'aws lambda put-function-concurrency --function-name render --reserved 6' },
      status: 'pending', decision: null, reason: null, created_at: ago(20), updated_at: ago(20),
    },
  ];

  let eid = 2000;
  const nextId = () => ++eid;
  const events: FactoryEventRow[] = [
    {
      id: nextId(), tenant_id: tenant, type: 'task.started', scope: 'gtm',
      payload: { external_id: 'task-4821' }, event_ts: ago(14), created_at: ago(14),
    },
    {
      id: nextId(), tenant_id: tenant, type: 'pr.review.completed', scope: 'product',
      payload: { pr_key: 'sully-k/factory-app#3', verdict: 'approve_with_nits' }, event_ts: ago(3), created_at: ago(3),
    },
    {
      id: nextId(), tenant_id: tenant, type: 'approval.requested', scope: 'finance',
      payload: { external_id: 'appr-991', tool: 'gmail.send' }, event_ts: ago(9), created_at: ago(9),
    },
    {
      id: nextId(), tenant_id: tenant, type: 'task.failed', scope: 'infra',
      payload: { external_id: 'task-4790', error: 'render-server OOM' }, event_ts: ago(292), created_at: ago(292),
    },
    {
      id: nextId(), tenant_id: tenant, type: 'task.done', scope: 'product',
      payload: { external_id: 'task-4798', result: 'Shipped build 9' }, event_ts: ago(120), created_at: ago(120),
    },
    {
      id: nextId(), tenant_id: tenant, type: 'heartbeat', scope: 'daemon',
      payload: { state: 'healthy' }, event_ts: ago(0.5), created_at: ago(0.5),
    },
  ];

  const health: FactoryHealthRow = {
    tenant_id: tenant, daemon_alive: true, last_heartbeat: ago(0.5), watchdog_state: 'healthy',
    created_at: ago(600), updated_at: ago(0.5),
  };

  return { tasks, prTasks, approvals, events, health };
}
